import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Collapse, Drawer, Input, List, Space, Typography, message } from 'antd';
import { ArrowUpOutlined, HistoryOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import ChatMarkdown from '../components/ChatMarkdown';
import ThinkingIndicator from '../components/ThinkingIndicator';
import { API_BASE_URL, getAuthHeaders } from '../api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

type Role = 'user' | 'assistant';

interface ChatMessage {
  role: Role;
  content: string;
  plotArtifacts?: PlotArtifact[];
  thinking?: ChatThinking;
}

interface PlotArtifact {
  filename: string;
  mime_type: string;
  image_base64: string;
}

interface ChatResponse {
  response: string;
  conversation_id?: string;
  route?: string;
  plot_artifacts?: PlotArtifact[];
  thinking?: ChatThinking;
}

interface ChatThinking {
  orchestrator: string;
  route: string;
  plan_steps: string[];
  tool_summary: Record<string, unknown>;
}

interface ConversationSummary {
  conversation_id: string;
  updated_at: string | null;
  preview: string;
}

interface NdjsonChatLine {
  type: string;
  message?: string;
  response?: string;
  conversation_id?: string;
  route?: string;
  plot_artifacts?: PlotArtifact[];
  thinking?: ChatThinking;
  error?: string;
}

/**
 * Parses NDJSON from a streaming chat response and forwards status lines to ``onStatus``.
 */
async function readNdjsonChatResponse(
  response: Response,
  onStatus: (message: string) => void
): Promise<ChatResponse> {
  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('Chat response had no body to read.');
  }
  const decoder = new TextDecoder();
  let buffer = '';
  let finalPayload: ChatResponse | null = null;

  const applyLine = (line: string): void => {
    if (!line.trim()) {
      return;
    }
    const obj = JSON.parse(line) as NdjsonChatLine;
    if (obj.type === 'status' && obj.message) {
      onStatus(obj.message);
    }
    if (obj.type === 'error') {
      throw new Error(obj.message || 'Chat request failed');
    }
    if (obj.type === 'result') {
      finalPayload = {
        response: obj.response ?? '',
        conversation_id: obj.conversation_id,
        route: obj.route,
        plot_artifacts: obj.plot_artifacts ?? [],
        thinking: obj.thinking,
      };
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (value != null) {
      buffer += decoder.decode(value as Uint8Array, { stream: !done });
    }
    if (done) {
      buffer.split('\n').forEach(applyLine);
      break;
    }
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';
    lines.forEach(applyLine);
  }
  if (!finalPayload) {
    throw new Error('Chat stream ended without a result.');
  }
  return finalPayload;
}

/**
 * POSTs to `/api/chat` with ``stream: true`` (NDJSON). Retries once on 502/503.
 */
async function postChatStreamWithRetry(
  trimmed: string,
  convId: string | null,
  headers: Record<string, string>,
  onStatus: (message: string) => void
): Promise<ChatResponse> {
  const url = `${API_BASE_URL}/api/chat`;
  const body = JSON.stringify({ prompt: trimmed, conversation_id: convId, stream: true });
  const init: RequestInit = {
    method: 'POST',
    headers: {
      ...headers,
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
    },
    body,
  };
  const run = () => fetch(url, init);
  let res = await run();
  if (res.status === 502 || res.status === 503) {
    await new Promise((r) => setTimeout(r, 400));
    res = await run();
  }
  if (!res.ok) {
    let errMsg = `Request failed (${res.status})`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data?.error) {
        errMsg = data.error;
      }
    } catch {
      /* non-JSON error body */
    }
    throw new Error(errMsg);
  }
  return readNdjsonChatResponse(res, onStatus);
}

const Chat: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState<ConversationSummary[]>([]);
  const [availableHeight, setAvailableHeight] = useState<number>(600);
  const [agentStatus, setAgentStatus] = useState<string | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const historyContainerRef = useRef<HTMLDivElement | null>(null);

  const canSend = useMemo(() => !loading && prompt.trim().length > 0, [loading, prompt]);

  useEffect(() => {
    const historyEl = historyContainerRef.current;
    if (!historyEl) return;
    historyEl.scrollTop = historyEl.scrollHeight;
  }, [messages]);

  useEffect(() => {
    const updateAvailableHeight = (): void => {
      const pageEl = pageRef.current;
      if (!pageEl) return;
      const top = pageEl.getBoundingClientRect().top;
      const computedHeight = Math.max(window.innerHeight - top, 320);
      setAvailableHeight(computedHeight);
    };

    updateAvailableHeight();
    window.addEventListener('resize', updateAvailableHeight);
    return () => window.removeEventListener('resize', updateAvailableHeight);
  }, []);

  useEffect(() => {
    document.body.classList.add('chat-page-body-lock');
    return () => {
      document.body.classList.remove('chat-page-body-lock');
    };
  }, []);

  useEffect(() => {
    if (!historyOpen) return;
    const loadList = async (): Promise<void> => {
      setHistoryLoading(true);
      try {
        const headers = await getAuthHeaders();
        const { data } = await axios.get<{ conversations: ConversationSummary[] }>(
          `${API_BASE_URL}/api/chat/conversations`,
          { headers }
        );
        setHistoryItems(data.conversations || []);
      } catch {
        message.error('Could not load conversation history.');
      } finally {
        setHistoryLoading(false);
      }
    };
    void loadList();
  }, [historyOpen]);

  const startNewChat = (): void => {
    setMessages([]);
    setConversationId(null);
    setPrompt('');
    setHistoryOpen(false);
  };

  const openConversation = async (id: string): Promise<void> => {
    setHistoryLoading(true);
    try {
      const headers = await getAuthHeaders();
      const { data } = await axios.get<{ messages: { role: Role; content: string }[] }>(
        `${API_BASE_URL}/api/chat/conversations/${encodeURIComponent(id)}/messages`,
        { headers }
      );
      setConversationId(id);
      setMessages(
        (data.messages || []).map((m) => ({
          role: m.role,
          content: m.content,
        }))
      );
      setHistoryOpen(false);
    } catch {
      message.error('Could not open this conversation.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const sendPrompt = async (text: string): Promise<void> => {
    const trimmed = text.trim();
    if (!trimmed) return;
    const headers = await getAuthHeaders();
    if (!('Authorization' in headers)) {
      message.error(
        'Could not get a sign-in token. Refresh the page or sign in again, then retry.'
      );
      return;
    }
    setLoading(true);
    setAgentStatus('Thinking…');
    setMessages((prev) => [...prev, { role: 'user', content: trimmed }]);
    setPrompt('');
    try {
      const data = await postChatStreamWithRetry(trimmed, conversationId, headers, (msg) => {
        setAgentStatus(msg);
      });
      if (data.conversation_id) {
        setConversationId(data.conversation_id);
      }
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: data.response,
          plotArtifacts: data.plot_artifacts || [],
          thinking: data.thinking,
        },
      ]);
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : undefined;
      message.error(errMsg || 'Failed to get response from TrackiAI.');
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'I could not generate a response right now. Please try again.' },
      ]);
    } finally {
      setLoading(false);
      setAgentStatus(null);
    }
  };

  return (
    <div className="chat-page" ref={pageRef} style={{ height: availableHeight }}>
      <div>
        <Title level={3} style={{ marginBottom: 8 }}>TrackiAI</Title>
        <Text type="secondary">
          Ask athletics data questions, generate reports, benchmark performance, and request web-sourced insights.
        </Text>
      </div>

      <Card
        title="Conversation"
        className="chat-conversation-card"
        extra={(
          <Space size="small" wrap>
            <Button
              type="default"
              icon={<PlusOutlined />}
              aria-label="Start new chat"
              onClick={startNewChat}
            >
              New chat
            </Button>
            <Button
              type="text"
              icon={<HistoryOutlined />}
              aria-label="Conversation history"
              onClick={() => setHistoryOpen(true)}
            />
          </Space>
        )}
      >
        <div className="chat-panel">
          <div className="chat-history" ref={historyContainerRef} aria-label="Chat history">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {messages.length === 0 ? (
                <Text type="secondary">Start by asking a question about athletes, competitions, or trends.</Text>
              ) : (
                <>
                  {messages.map((msg, idx) => (
                    <Card
                      key={`${msg.role}-${idx}`}
                      size="small"
                      className={msg.role === 'assistant' ? 'chat-assistant-bubble' : undefined}
                      style={{ background: msg.role === 'user' ? 'var(--pale-sky)' : 'var(--bright-sky)' }}
                    >
                      <Text strong>{msg.role === 'user' ? 'You' : 'Assistant'}</Text>
                      {msg.role === 'assistant' ? (
                        <div className="chat-markdown">
                          <ChatMarkdown content={msg.content} />
                          {(msg.plotArtifacts || []).map((artifact, imgIdx) => (
                            <img
                              key={`${artifact.filename}-${imgIdx}`}
                              className="chat-plot-image"
                              src={`data:${artifact.mime_type};base64,${artifact.image_base64}`}
                              alt={artifact.filename}
                            />
                          ))}
                          {msg.thinking && (
                            <Collapse
                              style={{ marginTop: 12 }}
                              items={[
                                {
                                  key: 'thinking',
                                  label: 'Thinking (orchestrator + execution plan)',
                                  children: (
                                    <div style={{ maxHeight: 220, overflow: 'auto', fontSize: 13 }}>
                                      <div><strong>Orchestrator:</strong> {msg.thinking.orchestrator}</div>
                                      <div><strong>Route:</strong> {msg.thinking.route}</div>
                                      <div style={{ marginTop: 8 }}><strong>Plan executed:</strong></div>
                                      <ol style={{ marginTop: 4, paddingLeft: 20 }}>
                                        {(msg.thinking.plan_steps || []).map((step, stepIdx) => (
                                          <li key={`${step}-${stepIdx}`}>{step}</li>
                                        ))}
                                      </ol>
                                      <div style={{ marginTop: 8 }}><strong>Tool summary:</strong></div>
                                      <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                                        {JSON.stringify(msg.thinking.tool_summary, null, 2)}
                                      </pre>
                                    </div>
                                  ),
                                },
                              ]}
                            />
                          )}
                        </div>
                      ) : (
                        <Paragraph style={{ marginTop: 8, marginBottom: 0 }}>{msg.content}</Paragraph>
                      )}
                    </Card>
                  ))}
                  {loading && (
                    <Card
                      size="small"
                      className="chat-assistant-bubble"
                      style={{ background: 'var(--bright-sky)' }}
                      aria-live="polite"
                      aria-busy="true"
                      aria-label="Assistant is working"
                    >
                      <Text strong>Assistant</Text>
                      <Text
                        type="secondary"
                        className="chat-agent-status"
                        style={{ display: 'block', marginTop: 8 }}
                      >
                        <ThinkingIndicator label={agentStatus} />
                      </Text>
                    </Card>
                  )}
                </>
              )}
            </Space>
          </div>
          <div className="chat-composer">
            <div className="chat-input-wrapper">
              <TextArea
                aria-label="Chat prompt"
                rows={4}
                value={prompt}
                onChange={(event) => setPrompt(event.target.value)}
                placeholder="Ask for insights, summaries, or a report draft..."
                onPressEnter={(event) => {
                  if (!event.shiftKey) {
                    event.preventDefault();
                    void sendPrompt(prompt);
                  }
                }}
              />
              <Button
                aria-label="Send message"
                className="chat-send-button"
                type="primary"
                shape="circle"
                icon={<ArrowUpOutlined />}
                onClick={() => void sendPrompt(prompt)}
                loading={loading}
                disabled={!canSend}
              />
            </div>
          </div>
        </div>
      </Card>

      <Drawer
        title="Conversation history"
        placement="right"
        width={320}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        destroyOnClose={false}
      >
        <Button type="primary" block onClick={startNewChat} style={{ marginBottom: 16 }}>
          New chat
        </Button>
        <List<ConversationSummary>
          loading={historyLoading}
          dataSource={historyItems}
          locale={{ emptyText: 'No saved conversations yet.' }}
          renderItem={(item) => (
            <List.Item
              style={{ cursor: 'pointer' }}
              onClick={() => void openConversation(item.conversation_id)}
            >
              <List.Item.Meta
                title={(item.preview || '(no preview)').slice(0, 120)}
                description={
                  item.updated_at
                    ? new Date(item.updated_at).toLocaleString()
                    : undefined
                }
              />
            </List.Item>
          )}
        />
      </Drawer>
    </div>
  );
};

export default Chat;
